/**
 * @audit GAP-FE-B005 · raw HTTP retained — see `shared/services/AUDIT_HTTP_DEBT.md`
 *
 * Severity: MEDIUM
 * Reason:  /assets/i18n/*.json — STATIC ASSET fetch, NOT an API call (re-classify, keep inline)
 *
 * When this service no longer holds `inject(HttpClient)` or hand-rolled
 * `this.http.X` calls (either by migrating to `@api` or sequestering
 * to a `*-bridge.service.ts`), remove this banner and the matching row
 * in `AUDIT_HTTP_DEBT.md`.
 */
import { Injectable, signal, computed, effect, inject, DestroyRef } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Subject, of, switchMap, tap, map, catchError } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggerService } from './logger.service';
import { AppStorageService } from './app-storage.service';

export type Language = 'en' | 'es' | 'fr' | 'de' | 'zh' | 'ja';

interface Translation {
  [key: string]: string | Translation;
}

interface LanguageConfig {
  code: Language;
  name: string;
  nativeName: string;
  direction: 'ltr' | 'rtl';
  dateFormat: string;
  numberFormat: {
    decimal: string;
    thousands: string;
    currency: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private readonly logger = inject(LoggerService);
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appStorage = inject(AppStorageService);

  /** Subject that serializes language load requests via switchMap */
  private readonly loadSubject = new Subject<Language>();

  /**
   * Tracks which languages have been fully loaded from HTTP.
   * The embedded English translations are set synchronously, but this set
   * tracks whether the full i18n JSON file has been fetched for a language.
   */
  private readonly httpLoadedLanguages = new Set<Language>();

  private translations = signal<Record<Language, Translation>>({
    en: {},
    es: {},
    fr: {},
    de: {},
    zh: {},
    ja: {}
  });

  private currentLang = signal<Language>('en');
  private fallbackLang: Language = 'en';

  // Language configurations
  private languages: LanguageConfig[] = [
    {
      code: 'en',
      name: 'English',
      nativeName: 'English',
      direction: 'ltr',
      dateFormat: 'MM/DD/YYYY',
      numberFormat: {
        decimal: '.',
        thousands: ',',
        currency: '$'
      }
    },
    {
      code: 'es',
      name: 'Spanish',
      nativeName: 'Espanol',
      direction: 'ltr',
      dateFormat: 'DD/MM/YYYY',
      numberFormat: {
        decimal: ',',
        thousands: '.',
        currency: '\u20AC'
      }
    },
    {
      code: 'fr',
      name: 'French',
      nativeName: 'Francais',
      direction: 'ltr',
      dateFormat: 'DD/MM/YYYY',
      numberFormat: {
        decimal: ',',
        thousands: ' ',
        currency: '\u20AC'
      }
    },
    {
      code: 'de',
      name: 'German',
      nativeName: 'Deutsch',
      direction: 'ltr',
      dateFormat: 'DD.MM.YYYY',
      numberFormat: {
        decimal: ',',
        thousands: '.',
        currency: '\u20AC'
      }
    },
    {
      code: 'zh',
      name: 'Chinese',
      nativeName: '\u4E2D\u6587',
      direction: 'ltr',
      dateFormat: 'YYYY/MM/DD',
      numberFormat: {
        decimal: '.',
        thousands: ',',
        currency: '\u00A5'
      }
    },
    {
      code: 'ja',
      name: 'Japanese',
      nativeName: '\u65E5\u672C\u8A9E',
      direction: 'ltr',
      dateFormat: 'YYYY/MM/DD',
      numberFormat: {
        decimal: '.',
        thousands: ',',
        currency: '\u00A5'
      }
    }
  ];

  // Computed values
  currentLanguage = computed(() => this.currentLang());
  availableLanguages = computed(() => this.languages);
  currentConfig = computed(() =>
    this.languages.find(l => l.code === this.currentLang()) || this.languages[0]
  );

  constructor() {
    // Load saved language preference
    // GAP-FE-C013: SSR-safe wrapper; preserves the 'language' key.
    const savedLang = this.appStorage.getItem('language') as Language;
    if (savedLang && this.languages.some(l => l.code === savedLang)) {
      this.currentLang.set(savedLang);
    }

    // Apply language direction using injected DOCUMENT token
    effect(() => {
      const config = this.currentConfig();
      this.document.documentElement.dir = config.direction;
      this.document.documentElement.lang = config.code;
    });

    // Set up switchMap pipeline to serialize language load requests.
    // switchMap cancels any in-flight HTTP request when a new language is requested,
    // preventing out-of-order responses from corrupting translation state.
    this.loadSubject.pipe(
      switchMap(lang => {
        // If this language was already fetched via HTTP, skip the network call
        if (this.httpLoadedLanguages.has(lang)) {
          return of(lang);
        }
        return this.http.get<Translation>(`assets/i18n/${lang}.json`).pipe(
          tap(data => {
            this.translations.update(t => ({
              ...t,
              [lang]: data
            }));
            this.httpLoadedLanguages.add(lang);
          }),
          map(() => lang),
          catchError(err => {
            this.logger.error(
              `Failed to load translations for ${lang}`,
              { component: 'TranslationService', language: lang },
              err
            );
            // For non-English languages, copy English translations as fallback
            if (lang !== 'en') {
              this.translations.update(t => ({
                ...t,
                [lang]: t['en']
              }));
            }
            return of(this.fallbackLang);
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(lang => {
      this.currentLang.set(lang);
      this.document.documentElement.lang = lang;
    });

    // Load translations
    this.loadTranslations();
  }

  /**
   * Set the current language.
   * Pushes to the loadSubject which serializes requests via switchMap,
   * ensuring rapid language switches cancel stale in-flight HTTP requests.
   */
  setLanguage(lang: Language): void {
    if (this.languages.some(l => l.code === lang)) {
      this.appStorage.setItem('language', lang);
      this.loadSubject.next(lang);
    }
  }

  /**
   * Get a translation by key
   */
  translate(key: string, params?: Record<string, unknown>): string {
    const translation = this.getTranslation(key, this.currentLang());

    if (!translation) {
      // Try fallback language
      const fallback = this.getTranslation(key, this.fallbackLang);
      if (fallback) {
        return this.interpolate(fallback, params);
      }

      // Return key if no translation found
      return key;
    }

    return this.interpolate(translation, params);
  }

  /**
   * Get a translation with instant update (for reactive templates)
   */
  instant(key: string, params?: Record<string, unknown>): string {
    return this.translate(key, params);
  }

  /**
   * Format a number according to current locale
   */
  formatNumber(value: number, decimals = 2): string {
    const config = this.currentConfig();
    const parts = value.toFixed(decimals).split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, config.numberFormat.thousands);
    const decimalPart = parts[1];

    return decimalPart
      ? `${integerPart}${config.numberFormat.decimal}${decimalPart}`
      : integerPart;
  }

  /**
   * Format currency according to current locale
   */
  formatCurrency(value: number): string {
    const config = this.currentConfig();
    const formatted = this.formatNumber(value, 2);
    return `${config.numberFormat.currency}${formatted}`;
  }

  /**
   * Format date according to current locale
   */
  formatDate(date: Date): string {
    const config = this.currentConfig();
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();

    return config.dateFormat
      .replace('DD', d)
      .replace('MM', m)
      .replace('YYYY', y.toString());
  }

  /**
   * Check if a translation exists
   */
  hasTranslation(key: string, lang?: Language): boolean {
    const targetLang = lang || this.currentLang();
    return !!this.getTranslation(key, targetLang);
  }

  /**
   * Load all translations.
   * Sets embedded English as immediate synchronous fallback, then pushes
   * the current language through the loadSubject pipeline for async loading.
   */
  private loadTranslations(): void {
    // Set embedded English translations as immediate fallback so the UI is never empty
    this.translations.update(t => ({
      ...t,
      en: this.getEnglishTranslations()
    }));

    // Push the current language through the serialized pipeline.
    // For English: fetches the full en.json to supplement embedded translations.
    // For other languages: fetches that language's JSON (falls back to en on error).
    this.loadSubject.next(this.currentLang());
  }

  /**
   * Get a translation by key and language
   */
  private getTranslation(key: string, lang: Language): string | null {
    const translations = this.translations()[lang];
    const keys = key.split('.');
    let current: unknown = translations;

    for (const k of keys) {
      if (current && typeof current === 'object' && Object.prototype.hasOwnProperty.call(current, k)) {
        current = (current as Record<string, unknown>)[k];
      } else {
        return null;
      }
    }

    return typeof current === 'string' ? current : null;
  }

  /**
   * Interpolate parameters in translation string
   */
  private interpolate(translation: string, params?: Record<string, unknown>): string {
    if (!params) {return translation;}

    return translation.replace(/\{\{(\w+)\}\}/g, (match, key) => params[key] !== undefined ? String(params[key]) : match);
  }

  /**
   * Embedded English translations used as an immediate synchronous fallback.
   * The canonical source of truth is assets/i18n/en.json; this embedded copy
   * ensures the UI has translations available before the first HTTP response.
   */
  private getEnglishTranslations(): Translation {
    return {
      common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        create: 'Create',
        update: 'Update',
        search: 'Search',
        filter: 'Filter',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        warning: 'Warning',
        info: 'Information',
        confirm: 'Confirm',
        yes: 'Yes',
        no: 'No'
      },
      navigation: {
        dashboard: 'Dashboard',
        orders: 'Orders',
        positions: 'Positions',
        risk: 'Risk Management',
        strategies: 'Strategies',
        backtesting: 'Backtesting',
        settings: 'Settings',
        profile: 'Profile',
        logout: 'Logout'
      },
      auth: {
        login: 'Login',
        register: 'Register',
        forgotPassword: 'Forgot Password',
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        rememberMe: 'Remember Me',
        signIn: 'Sign In',
        signUp: 'Sign Up',
        resetPassword: 'Reset Password'
      },
      dashboard: {
        title: 'Trading Dashboard',
        totalReturn: 'Total Return',
        activePositions: 'Active Positions',
        todaysPnl: "Today's P&L",
        openOrders: 'Open Orders',
        recentTrades: 'Recent Trades',
        performance: 'Performance',
        riskMetrics: 'Risk Metrics'
      },
      trading: {
        buy: 'Buy',
        sell: 'Sell',
        quantity: 'Quantity',
        price: 'Price',
        total: 'Total',
        orderType: 'Order Type',
        limitOrder: 'Limit',
        marketOrder: 'Market',
        stopOrder: 'Stop',
        symbol: 'Symbol',
        side: 'Side',
        status: 'Status',
        filled: 'Filled',
        pending: 'Pending',
        cancelled: 'Cancelled'
      },
      messages: {
        saveSuccess: 'Changes saved successfully',
        deleteConfirm: 'Are you sure you want to delete this item?',
        errorOccurred: 'An error occurred. Please try again.',
        networkError: 'Network error. Please check your connection.',
        validationError: 'Please check the form for errors.',
        sessionExpired: 'Your session has expired. Please login again.'
      }
    };
  }
}
